/**
 * DisplayHeading — page hero heading.
 *
 * Two visual modes:
 *   `sans` (default) — Inter 42 px / 700 — used by Vozidla & workspace crowns.
 *   `serif`          — Fraunces 56–64 px / 400 italic 300 — used by editorial
 *                      moments (Nový zákazník form, leasing wizard).
 *
 * Wrap italics in `<em>` inside `children` for the design's display rhythm.
 *
 * Source: .agents/design/project/artboard-radical.jsx:115-123 (sans)
 *         .agents/design/project/artboard-radical-zakaznik-form.jsx:88-93 (serif)
 */

import type { ElementType, ReactNode } from 'react';

interface DisplayHeadingProps {
  children: ReactNode;
  variant?: 'sans' | 'serif';
  /** HTML tag to render. Defaults to `h1`. */
  as?: ElementType;
  className?: string;
}

const VARIANT: Record<'sans' | 'serif', string> = {
  sans:
    "font-sans font-bold text-[42px] tracking-[-0.04em] leading-none text-ink max-sm:text-[28px] [&>em]:not-italic [&>em]:font-medium [&>em]:text-ink-2",
  serif:
    "font-display font-normal text-[56px] tracking-[-0.03em] leading-[0.95] text-ink max-sm:text-[32px] [&>em]:font-light [&>em]:text-ink-3",
};

export function DisplayHeading({
  children,
  variant = 'sans',
  as,
  className = '',
}: DisplayHeadingProps) {
  const Tag = (as ?? 'h1') as ElementType;
  return <Tag className={`${VARIANT[variant]} ${className}`}>{children}</Tag>;
}
